import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { 
  CheckCircleIcon, 
  XCircleIcon, 
  ShieldExclamationIcon, 
  InboxIcon, 
  StarIcon, 
  PencilSquareIcon, 
  CheckIcon, 
  ArrowUturnLeftIcon,
  BuildingOfficeIcon,
  UsersIcon
} from '@heroicons/react/24/solid';

// --- Sub-component for Pending Campaigns ---
const PendingCampaignCard = ({ campaign, onUpdate, onEditReason }) => {
    const [notes, setNotes] = useState('');
    const [isExpanded, setIsExpanded] = useState(false);
    const [isEditingReason, setIsEditingReason] = useState(false);
    const [editedReason, setEditedReason] = useState(campaign.reason);

    useEffect(() => {
        setEditedReason(campaign.reason);
    }, [campaign.reason]);

    const handleApprove = () => {
        onUpdate(campaign.id, 'approved', 'Campaign approved via admin panel.');
    };

    const handleReject = () => {
        if (!notes.trim()) {
            alert('A reason is required to reject a campaign.');
            return;
        }
        onUpdate(campaign.id, 'rejected', notes);
    };

    const handleSaveReason = () => {
        if (editedReason.trim()) {
            onEditReason(campaign.id, editedReason);
            setIsEditingReason(false);
        } else {
            alert("Reason cannot be empty.");
        }
    };

    const handleCancelEdit = () => {
        setIsEditingReason(false);
        setEditedReason(campaign.reason);
    };

    const profileImageUrl = campaign.profile_photo_url || `https://ui-avatars.com/api/?name=${campaign.first_name}+${campaign.last_name}&background=e46809&color=fff`;

    return (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 overflow-hidden">
            <button 
                onClick={() => setIsExpanded(!isExpanded)} 
                className="w-full text-left p-4 flex justify-between items-center hover:bg-gray-50 dark:hover:bg-gray-700/50"
            >
                <div className="flex items-center gap-4">
                    <img src={profileImageUrl} alt="Recipient" className="w-12 h-12 rounded-full object-cover" />
                    <div>
                        <h3 className="font-bold text-lg text-gray-800 dark:text-white">{campaign.product_name}</h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400">For: {campaign.first_name} {campaign.last_name}</p>
                    </div>
                </div>
                <span className={`transform transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}>▼</span>
            </button>

            {isExpanded && (
                <div className="p-4 border-t border-gray-200 dark:border-gray-700 space-y-4">
                    <div>
                        <label className="font-bold text-gray-700 dark:text-gray-300">Phone Number:</label>
                        <p className="text-gray-600 dark:text-gray-300">{campaign.phone_number || 'Not provided'}</p>
                    </div>
                    <div className="space-y-2">
                        <div className="flex justify-between items-center">
                            <label className="font-bold text-gray-700 dark:text-gray-300">Reason:</label>
                            <div className="flex items-center gap-4">
                                {isEditingReason ? (
                                    <>
                                        <button onClick={handleSaveReason} className="flex items-center text-sm text-green-500 hover:underline">
                                            <CheckIcon className="w-4 h-4 mr-1" /> Save Changes
                                        </button>
                                        <button onClick={handleCancelEdit} className="flex items-center text-sm text-red-500 hover:underline">
                                            <XCircleIcon className="w-4 h-4 mr-1" /> Cancel
                                        </button>
                                    </>
                                ) : (
                                    <button onClick={() => setIsEditingReason(true)} className="flex items-center text-sm text-blue-500 hover:underline">
                                        <PencilSquareIcon className="w-4 h-4 mr-1" /> Edit
                                    </button>
                                )}
                            </div>
                        </div>
                        {isEditingReason ? (
                            <textarea
                                value={editedReason}
                                onChange={(e) => setEditedReason(e.target.value)}
                                className="w-full p-2 border rounded-md dark:bg-gray-700 dark:border-gray-600 focus:ring-2 focus:ring-orange-500"
                                rows="4"
                            />
                        ) : (
                            <p className="italic text-gray-600 dark:text-gray-300">"{editedReason}"</p>
                        )}
                    </div>
                    
                    <a href={campaign.id_document_url} target="_blank" rel="noopener noreferrer" className="text-blue-500 underline text-sm">View ID Document</a>
                    
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Rejection Notes (if applicable):</label>
                        <textarea 
                            className="mt-1 w-full p-2 border rounded-md dark:bg-gray-700 dark:border-gray-600" 
                            rows="2"
                            placeholder="Provide a clear reason for rejection..."
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                        />
                    </div>
                    <div className="flex gap-4">
                        <button onClick={handleApprove} className="flex-1 flex items-center justify-center py-2 px-4 bg-green-500 text-white font-semibold rounded-lg hover:bg-green-600 transition-colors">
                            <CheckCircleIcon className="w-5 h-5 mr-2" />
                            Approve
                        </button>
                        <button onClick={handleReject} className="flex-1 flex items-center justify-center py-2 px-4 bg-red-500 text-white font-semibold rounded-lg hover:bg-red-600 transition-colors">
                            <XCircleIcon className="w-5 h-5 mr-2" />
                            Reject
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

// --- Sub-component for Approved Campaigns ---
const ApprovedCampaignCard = ({ campaign, onFeature, onUnapprove }) => (
    <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 flex justify-between items-center gap-2">
        <div>
            <h3 className="font-bold text-gray-800 dark:text-white">{campaign.product_name}</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">For: {campaign.first_name} {campaign.last_name}</p>
            <p className="text-sm text-gray-500 dark:text-gray-400">Phone: {campaign.phone_number || 'Not provided'}</p>
        </div>
        <div className="flex gap-2">
            <button 
                onClick={() => onUnapprove(campaign.id)} 
                className="flex items-center p-2 bg-yellow-500 text-white font-semibold rounded-lg hover:bg-yellow-600 transition-colors"
                title="Move back to Pending"
            >
                <ArrowUturnLeftIcon className="w-5 h-5" />
            </button>
            <button 
                onClick={() => onFeature(campaign.id)} 
                className="flex items-center py-2 px-4 bg-blue-500 text-white font-semibold rounded-lg hover:bg-blue-600 transition-colors text-sm"
            >
                <StarIcon className="w-5 h-5 mr-1" />
                Feature
            </button>
        </div>
    </div>
);

// --- Sub-component for Featured Campaigns ---
const FeaturedCampaignCard = ({ campaign, onUnfeature }) => (
    <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 flex justify-between items-center">
        <div>
            <h3 className="font-bold text-gray-800 dark:text-white">{campaign.product_name}</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">For: {campaign.first_name} {campaign.last_name}</p>
            <p className="text-sm text-gray-500 dark:text-gray-400">Phone: {campaign.phone_number || 'Not provided'}</p>
        </div>
        <button 
            onClick={() => onUnfeature(campaign.id)} 
            className="flex items-center py-2 px-4 bg-red-500 text-white font-semibold rounded-lg hover:bg-red-600 transition-colors"
        >
            <XCircleIcon className="w-5 h-5 mr-2" />
            Un-feature
        </button>
    </div>
);

// --- Sub-component for Organization Pending Campaigns ---
const PendingOrgCampaignCard = ({ campaign, onUpdate }) => {
    const [notes, setNotes] = useState('');
    const [isExpanded, setIsExpanded] = useState(false);

    const handleApprove = () => {
        onUpdate(campaign.id, 'approved', 'Organization campaign approved via admin panel.');
    };

    const handleReject = () => {
        if (!notes.trim()) {
            alert('A reason is required to reject a campaign.');
            return;
        }
        onUpdate(campaign.id, 'rejected', notes);
    };

    return (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 overflow-hidden">
            <button 
                onClick={() => setIsExpanded(!isExpanded)} 
                className="w-full text-left p-4 flex justify-between items-center hover:bg-gray-50 dark:hover:bg-gray-700/50"
            >
                <div>
                    <h3 className="font-bold text-lg text-gray-800 dark:text-white">{campaign.product_name}</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                        Organization: {campaign.organization_name} ({campaign.first_name} {campaign.last_name})
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                        Quantity: {campaign.quantity} units (${campaign.total_price})
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                        Phone: {campaign.phone_number || 'Not provided'}
                    </p>
                </div>
                <span className={`transform transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}>▼</span>
            </button>

            {isExpanded && (
                <div className="p-4 border-t border-gray-200 dark:border-gray-700 space-y-4">
                    <div>
                        <label className="font-bold text-gray-700 dark:text-gray-300">Reason:</label>
                        <p className="italic text-gray-600 dark:text-gray-300">"{campaign.reason}"</p>
                    </div>
                    
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Rejection Notes (if applicable):</label>
                        <textarea 
                            className="mt-1 w-full p-2 border rounded-md dark:bg-gray-700 dark:border-gray-600" 
                            rows="2"
                            placeholder="Provide a clear reason for rejection..."
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                        />
                    </div>
                    <div className="flex gap-4">
                        <button onClick={handleApprove} className="flex-1 flex items-center justify-center py-2 px-4 bg-green-500 text-white font-semibold rounded-lg hover:bg-green-600 transition-colors">
                            <CheckCircleIcon className="w-5 h-5 mr-2" />
                            Approve
                        </button>
                        <button onClick={handleReject} className="flex-1 flex items-center justify-center py-2 px-4 bg-red-500 text-white font-semibold rounded-lg hover:bg-red-600 transition-colors">
                            <XCircleIcon className="w-5 h-5 mr-2" />
                            Reject
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

// --- Sub-component for Active Organization Campaigns ---
const ActiveOrgCampaignCard = ({ campaign, onViewDonations }) => {
    const progressPercentage = (campaign.units_fulfilled / campaign.quantity) * 100;

    return (
        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-md border border-gray-200 dark:border-gray-700">
            <div className="flex justify-between items-start mb-3">
                <div>
                    <h3 className="font-bold text-lg text-gray-800 dark:text-white">{campaign.product_name}</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                        Organization: {campaign.organization_name}
                    </p>
                     <p className="text-sm text-gray-500 dark:text-gray-400">
                        Phone: {campaign.phone_number || 'Not provided'}
                    </p>
                </div>
                <button 
                    onClick={() => onViewDonations(campaign.id)} 
                    className="flex items-center py-1 px-3 bg-blue-500 text-white font-semibold rounded-lg hover:bg-blue-600 transition-colors text-sm"
                >
                    View Donations
                </button>
            </div>
            
            <div className="mb-2">
                <div className="flex justify-between text-sm mb-1">
                    <span>Progress: {campaign.units_fulfilled || 0}/{campaign.quantity} units</span>
                    <span>${campaign.amount_raised || 0} of ${campaign.total_price}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2.5 dark:bg-gray-700">
                    <div 
                        className="bg-green-600 h-2.5 rounded-full" 
                        style={{ width: `${progressPercentage}%` }}
                    ></div>
                </div>
            </div>
            
            <div className="flex justify-between text-sm text-gray-500 dark:text-gray-400">
                <span>Contact: {campaign.first_name} {campaign.last_name}</span>
                <span>{campaign.email}</span>
            </div>
        </div>
    );
};

// --- Organization Campaigns Admin Component ---
const OrganizationCampaignsAdmin = ({ token }) => {
    const [pendingCampaigns, setPendingCampaigns] = useState([]);
    const [activeCampaigns, setActiveCampaigns] = useState([]);
    const [selectedCampaign, setSelectedCampaign] = useState(null);
    const [donations, setDonations] = useState([]);
    const [loading, setLoading] = useState(true);

    const fetchData = useCallback(async () => {
        if (!token) return;
        setLoading(true);
        try {
            const [pendingRes, activeRes] = await Promise.all([
                fetch(`${process.env.REACT_APP_API_URL}/admin/org-campaigns/pending`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                }),
                fetch(`${process.env.REACT_APP_API_URL}/admin/org-campaigns/active`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                })
            ]);

            if (pendingRes.ok) setPendingCampaigns(await pendingRes.json());
            if (activeRes.ok) setActiveCampaigns(await activeRes.json());
        } catch (error) {
            console.error('Error fetching organization campaigns:', error);
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => { 
        fetchData(); 
    }, [fetchData]);

    const updateStatus = async (id, status, notes = '') => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/org-campaigns/${id}/status`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ status, adminNotes: notes })
            });

            if (response.ok) {
                fetchData();
            }
        } catch (error) {
            console.error('Error updating status:', error);
        }
    };

    const viewDonations = async (campaignId) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/org-campaigns/${campaignId}/donations`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (response.ok) {
                const donationData = await response.json();
                setDonations(donationData);
                setSelectedCampaign(campaignId);
            }
        } catch (error) {
            console.error('Error fetching donations:', error);
        }
    };

    if (loading) return <div className="text-center text-xl p-8 text-gray-600 dark:text-gray-300">Loading Organization Campaigns...</div>;

    return (
        <div className="space-y-8">
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-8 items-start">
                <section>
                    <div className="flex items-center mb-4">
                        <ShieldExclamationIcon className="w-8 h-8 text-yellow-500 mr-3" />
                        <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-200">Pending Organization Campaigns ({pendingCampaigns.length})</h2>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-800/50 p-6 rounded-xl space-y-4">
                        {pendingCampaigns.length > 0 ? (
                            pendingCampaigns.map(campaign => (
                                <PendingOrgCampaignCard 
                                    key={campaign.id} 
                                    campaign={campaign} 
                                    onUpdate={updateStatus} 
                                />
                            ))
                        ) : ( 
                            <div className="text-center py-8">
                                <InboxIcon className="w-16 h-16 mx-auto text-gray-400" />
                                <p className="mt-4 text-lg text-gray-500">No pending organization campaigns.</p>
                            </div> 
                        )}
                    </div>
                </section>

                <section>
                    <div className="flex items-center mb-4">
                        <CheckCircleIcon className="w-8 h-8 text-green-500 mr-3" />
                        <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-200">Active Organization Campaigns ({activeCampaigns.length})</h2>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-800/50 p-6 rounded-xl space-y-4">
                        {activeCampaigns.length > 0 ? (
                            activeCampaigns.map(campaign => (
                                <ActiveOrgCampaignCard 
                                    key={campaign.id} 
                                    campaign={campaign} 
                                    onViewDonations={viewDonations} 
                                />
                            ))
                        ) : ( 
                            <div className="text-center py-8">
                                <InboxIcon className="w-16 h-16 mx-auto text-gray-400" />
                                <p className="mt-4 text-lg text-gray-500">No active organization campaigns.</p>
                            </div> 
                        )}
                    </div>
                </section>
            </div>

            {/* Donations Modal */}
            {selectedCampaign && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white dark:bg-gray-800 p-6 rounded-lg w-11/12 max-w-4xl max-h-[80vh] overflow-y-auto">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-xl font-semibold text-gray-800 dark:text-white">Donations for Campaign #{selectedCampaign}</h2>
                            <button 
                                onClick={() => {
                                    setSelectedCampaign(null);
                                    setDonations([]);
                                }} 
                                className="text-red-500 hover:text-red-700"
                            >
                                Close
                            </button>
                        </div>
                        <div className="overflow-x-auto">
                            {donations.length > 0 ? (
                                <table className="w-full text-sm text-left text-gray-500 dark:text-gray-400">
                                    <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
                                        <tr>
                                            <th className="px-4 py-3">Donor</th>
                                            <th className="px-4 py-3">Phone</th>
                                            <th className="px-4 py-3">Units Funded</th>
                                            <th className="px-4 py-3">Amount</th>
                                            <th className="px-4 py-3">Date</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {donations.map(donation => (
                                            <tr key={donation.id} className="bg-white border-b dark:bg-gray-800 dark:border-gray-700">
                                                <td className="px-4 py-3 font-medium text-gray-900 dark:text-white">
                                                    {donation.first_name} {donation.last_name}<br />
                                                    <span className="text-gray-500 text-xs">{donation.email}</span>
                                                </td>
                                                <td className="px-4 py-3">{donation.phone_number || 'N/A'}</td>
                                                <td className="px-4 py-3">{donation.units_funded}</td>
                                                <td className="px-4 py-3">${donation.amount}</td>
                                                <td className="px-4 py-3">{new Date(donation.created_at).toLocaleDateString()}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            ) : (
                                <p className="text-center py-4">No donations yet for this campaign.</p>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

// --- Main Admin Dashboard Page Component ---
export default function AdminDashboardPage() {
    const { token } = useAuth();
    const [pending, setPending] = useState([]);
    const [approved, setApproved] = useState([]);
    const [featured, setFeatured] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [activeTab, setActiveTab] = useState('recipient');

    const fetchData = useCallback(async () => {
        if (!token || activeTab !== 'recipient') return;
        setLoading(true);
        try {
            const [pendingRes, approvedRes, featuredRes] = await Promise.all([
                fetch(`${process.env.REACT_APP_API_URL}/admin/campaigns/pending`, { headers: { 'Authorization': `Bearer ${token}` }}),
                fetch(`${process.env.REACT_APP_API_URL}/admin/campaigns/approved`, { headers: { 'Authorization': `Bearer ${token}` }}),
                fetch(`${process.env.REACT_APP_API_URL}/admin/campaigns/featured`, { headers: { 'Authorization': `Bearer ${token}` }})
            ]);
            if (!pendingRes.ok || !approvedRes.ok || !featuredRes.ok) throw new Error('Failed to fetch admin data.');

            const pendingData = await pendingRes.json();
            const approvedData = await approvedRes.json();
            const featuredData = await featuredRes.json();
            
            setPending(pendingData);
            setApproved(approvedData);
            setFeatured(featuredData);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [token, activeTab]);

    useEffect(() => { 
        if (activeTab === 'recipient') {
            fetchData(); 
        }
    }, [fetchData, activeTab]);

    const handleUpdate = async (id, status, notes) => {
        await fetch(`${process.env.REACT_APP_API_URL}/admin/campaigns/${id}/status`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            body: JSON.stringify({ status, adminNotes: notes })
        });
        fetchData();
    };

    const handleFeature = async (campaignId) => {
        await fetch(`${process.env.REACT_APP_API_URL}/admin/campaigns/feature`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            body: JSON.stringify({ campaignId })
        });
        fetchData();
    };
    
    const handleUnfeature = async (campaignId) => {
        await fetch(`${process.env.REACT_APP_API_URL}/admin/campaigns/unfeature`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            body: JSON.stringify({ campaignId })
        });
        fetchData();
    };

    const handleEditReason = async (id, reason) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/campaigns/${id}/reason`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ reason })
            });
            if (!response.ok) {
                const errData = await response.json();
                throw new Error(errData.message || 'Failed to update reason.');
            }
            fetchData();
        } catch (err) {
            console.error(err);
            alert(err.message);
        }
    };
    
    if (loading && activeTab === 'recipient') return <div className="text-center text-xl p-8 text-gray-600 dark:text-gray-300">Loading Dashboard...</div>;
    if (error) return <p className="text-center text-xl p-8 text-red-500">{error}</p>;

    return (
        <div className="space-y-6">
            <h1 className="text-4xl font-extrabold text-gray-900 dark:text-white">Admin Dashboard</h1>

            {/* Tab Navigation */}
            <div className="border-b border-gray-200 dark:border-gray-700">
                <nav className="flex space-x-8">
                    <button
                        onClick={() => setActiveTab('recipient')}
                        className={`py-2 px-1 border-b-2 font-medium text-sm ${
                            activeTab === 'recipient'
                                ? 'border-orange-500 text-orange-600 dark:text-orange-400'
                                : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
                        }`}
                    >
                        <UsersIcon className="w-5 h-5 inline mr-2" />
                        Recipient Campaigns
                    </button>
                    <button
                        onClick={() => setActiveTab('organization')}
                        className={`py-2 px-1 border-b-2 font-medium text-sm ${
                            activeTab === 'organization'
                                ? 'border-orange-500 text-orange-600 dark:text-orange-400'
                                : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
                        }`}
                    >
                        <BuildingOfficeIcon className="w-5 h-5 inline mr-2" />
                        Organization Campaigns
                    </button>
                </nav>
            </div>

            {activeTab === 'recipient' ? (
                <div className="grid grid-cols-1 xl:grid-cols-3 gap-8 items-start">
                    <section className="xl:col-span-1">
                        <div className="flex items-center mb-4">
                            <ShieldExclamationIcon className="w-8 h-8 text-yellow-500 mr-3" />
                            <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-200">Pending ({pending.length})</h2>
                        </div>
                        <div className="bg-gray-50 dark:bg-gray-800/50 p-6 rounded-xl space-y-4">
                            {pending.length > 0 ? (
                                pending.map(campaign => <PendingCampaignCard key={campaign.id} campaign={campaign} onUpdate={handleUpdate} onEditReason={handleEditReason} />)
                            ) : ( <div className="text-center py-8"><InboxIcon className="w-16 h-16 mx-auto text-gray-400" /><p className="mt-4 text-lg text-gray-500">The approval queue is empty.</p></div> )}
                        </div>
                    </section>

                    <section className="xl:col-span-2 grid grid-cols-1 lg:grid-cols-2 gap-8">
                        <div>
                            <div className="flex items-center mb-4">
                                <CheckCircleIcon className="w-8 h-8 text-green-500 mr-3" />
                                <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-200">Approved ({approved.length})</h2>
                            </div>
                            <div className="bg-gray-50 dark:bg-gray-800/50 p-6 rounded-xl space-y-4">
                                {approved.length > 0 ? (
                                    approved.map(campaign => <ApprovedCampaignCard key={campaign.id} campaign={campaign} onFeature={handleFeature} onUnapprove={(id) => handleUpdate(id, 'pending', 'Moved back to pending by admin.')} />)
                                ) : ( <div className="text-center py-8"><InboxIcon className="w-16 h-16 mx-auto text-gray-400" /><p className="mt-4 text-lg text-gray-500">No approved campaigns waiting to be featured.</p></div> )}
                            </div>
                        </div>
                        <div>
                            <div className="flex items-center mb-4">
                                <StarIcon className="w-8 h-8 text-blue-500 mr-3" />
                                <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-200">Featured ({featured.length})</h2>
                            </div>
                            <div className="bg-gray-50 dark:bg-gray-800/50 p-6 rounded-xl space-y-4">
                                {featured.length > 0 ? (
                                    featured.map(campaign => <FeaturedCampaignCard key={campaign.id} campaign={campaign} onUnfeature={handleUnfeature} />)
                                ) : ( <div className="text-center py-8"><InboxIcon className="w-16 h-16 mx-auto text-gray-400" /><p className="mt-4 text-lg text-gray-500">No campaigns are currently featured.</p></div> )}
                            </div>
                        </div>
                    </section>
                </div>
            ) : (
                <OrganizationCampaignsAdmin token={token} />
            )}
        </div>
    );
}